import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createWalletClient,
  custom,
  decodeEventLog,
  getAddress,
  type Address,
  type Hash,
} from "viem";
import { base } from "viem/chains";
import { AdminApp } from "./AdminApp";
import { ClaimApp } from "./ClaimApp";
import { DeployApp } from "./DeployApp";
import { baseClient, ethereumClient } from "./chain";
import {
  BURN_TOKEN,
  HARDWARE_COLLECTION,
  LEGACY_BURNER,
  LEGACY_VECTOR,
  burnerAbi,
  erc1155Abi,
  hardwareCollectionAbi,
  legacyBurnerAbi,
} from "./contracts";
import {
  discoverDeploymentConfig,
  isDeploymentConfigured,
  type DeploymentConfig,
} from "./deployment";

const gallery = [
  ...Array.from({ length: 24 }, (_, index) => `/gallery/Project_20241230_${String(index + 53).padStart(4, "0")}.jpg`),
  ...Array.from({ length: 4 }, (_, index) => `/gallery/Project_20241231_${String(index + 1).padStart(4, "0")}.jpg`),
];
const MAX_HARDWARE_SUPPLY = 544n;

type TransactionState =
  | { kind: "idle" }
  | { kind: "pending"; message: string; hash?: Hash; chain?: "base" | "ethereum" }
  | { kind: "success"; message: string; hash: Hash; chain: "base" | "ethereum"; redemptionId?: Hash }
  | { kind: "error"; message: string };

type PendingRedemption = {
  transactionHash: Hash;
  logIndex: number;
  redemptionId: Hash;
};

export function App() {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  if (path === "/admin") return <AdminApp />;
  if (path === "/deploy") return <DeployApp />;
  if (path === "/claim") return <ClaimApp />;
  return <RedeemApp />;
}

function RedeemApp() {
  const [config, setConfig] = useState<DeploymentConfig>({});
  const [account, setAccount] = useState<Address>();
  const [balance, setBalance] = useState<bigint>(0n);
  const [cost, setCost] = useState<bigint>(232n);
  const [burnerPaused, setBurnerPaused] = useState(false);
  const [maximum, setMaximum] = useState("232");
  const [approved, setApproved] = useState(false);
  const [transaction, setTransaction] = useState<TransactionState>({ kind: "idle" });
  const [pendingRedemption, setPendingRedemption] = useState<PendingRedemption | null>(() => {
    try {
      return JSON.parse(localStorage.getItem("hardware:pending-redemption") ?? "null");
    } catch {
      return null;
    }
  });

  const shortAccount = useMemo(
    () => (account ? `${account.slice(0, 6)}…${account.slice(-4)}` : "Connect wallet"),
    [account],
  );
  const burnerAddress = config.burnerAddress;
  const isConfigured = isDeploymentConfigured(config);

  useEffect(() => {
    discoverDeploymentConfig({ requireActivation: true })
      .then(setConfig)
      .catch((error) => setTransaction({ kind: "error", message: friendlyError(error) }));
  }, []);

  const refresh = useCallback(async (address?: Address) => {
    if (!burnerAddress) return;
    const [nextCost, nextPaused] = await Promise.all([
      baseClient.readContract({ address: burnerAddress, abi: burnerAbi, functionName: "currentCost" }),
      baseClient.readContract({ address: burnerAddress, abi: burnerAbi, functionName: "paused" }),
    ]);
    setCost(nextCost);
    setBurnerPaused(nextPaused);
    setMaximum((current) => (BigInt(current || 0) < nextCost ? nextCost.toString() : current));
    if (!address) return;
    const [nextBalance, nextApproval] = await Promise.all([
      baseClient.readContract({ address: BURN_TOKEN, abi: erc1155Abi, functionName: "balanceOf", args: [address, 1n] }),
      baseClient.readContract({
        address: BURN_TOKEN,
        abi: erc1155Abi,
        functionName: "isApprovedForAll",
        args: [address, burnerAddress],
      }),
    ]);
    setBalance(nextBalance);
    setApproved(nextApproval);
  }, [burnerAddress]);

  useEffect(() => {
    refresh(account).catch((error) => setTransaction({ kind: "error", message: friendlyError(error) }));
  }, [account, refresh]);

  useEffect(() => {
    if (!window.ethereum) return;
    window.ethereum.request({ method: "eth_accounts" }).then((accounts) => {
      const first = (accounts as string[])[0];
      if (first) setAccount(getAddress(first));
    });
  }, []);

  async function connect() {
    if (!isConfigured) {
      setTransaction({ kind: "error", message: "The independent contracts have not been deployed yet." });
      return;
    }
    if (!window.ethereum) {
      setTransaction({ kind: "error", message: "Install or open an Ethereum wallet to continue." });
      return;
    }
    try {
      const accounts = (await window.ethereum.request({ method: "eth_requestAccounts" })) as string[];
      setAccount(getAddress(accounts[0]));
      await switchToBase();
    } catch (error) {
      setTransaction({ kind: "error", message: friendlyError(error) });
    }
  }

  async function approve() {
    if (!account || !window.ethereum || !burnerAddress) return;
    try {
      await switchToBase();
      const wallet = createWalletClient({ account, chain: base, transport: custom(window.ethereum) });
      setTransaction({ kind: "pending", message: "Approve the Hardware redemption contract in your wallet." });
      const hash = await wallet.writeContract({
        address: BURN_TOKEN,
        abi: erc1155Abi,
        functionName: "setApprovalForAll",
        args: [burnerAddress, true],
      });
      setTransaction({ kind: "pending", message: "Waiting for approval confirmation…", hash, chain: "base" });
      await baseClient.waitForTransactionReceipt({ hash });
      await refresh(account);
      setTransaction({ kind: "success", message: "Approval confirmed. You can now redeem.", hash, chain: "base" });
    } catch (error) {
      setTransaction({ kind: "error", message: friendlyError(error) });
    }
  }

  async function redeem() {
    if (!account || !window.ethereum || !burnerAddress) return;
    const maximumBurn = BigInt(maximum || 0);
    if (cost > MAX_HARDWARE_SUPPLY) {
      setTransaction({ kind: "error", message: "Hardware is sold out. No more 1988 can be burned through this contract." });
      return;
    }
    if (maximumBurn < cost) {
      setTransaction({ kind: "error", message: `Maximum burn must be at least ${cost}.` });
      return;
    }
    if (balance < cost) {
      setTransaction({ kind: "error", message: `You need ${cost} tokens; this wallet owns ${balance}.` });
      return;
    }
    try {
      await switchToBase();
      const wallet = createWalletClient({ account, chain: base, transport: custom(window.ethereum) });
      setTransaction({ kind: "pending", message: `Confirm the burn of ${cost} × 1988 in your wallet.` });
      const hash = await wallet.writeContract({
        address: burnerAddress,
        abi: burnerAbi,
        functionName: "redeem",
        args: [maximumBurn],
      });
      setTransaction({ kind: "pending", message: "Burn submitted. Waiting for Base confirmation…", hash, chain: "base" });
      const receipt = await baseClient.waitForTransactionReceipt({ hash });
      const log = receipt.logs.find((candidate) => candidate.address.toLowerCase() === burnerAddress.toLowerCase());
      let redemptionId: Hash | undefined;
      if (log) {
        try {
          const decoded = decodeEventLog({ abi: burnerAbi, data: log.data, topics: log.topics });
          if (decoded.eventName === "RedemptionInitiated") redemptionId = decoded.args.redemptionId;
        } catch {
          // Transaction success remains authoritative if a wallet/provider omits decoded logs.
        }
      }
      await refresh(account);
      if (redemptionId && log) {
        const pending = { transactionHash: hash, logIndex: log.logIndex, redemptionId };
        localStorage.setItem("hardware:pending-redemption", JSON.stringify(pending));
        setPendingRedemption(pending);
      }
      setTransaction({
        kind: "success",
        message: "Burn confirmed. Your claim is ready for the owner’s Ledger approval.",
        hash,
        chain: "base",
        redemptionId,
      });
    } catch (error) {
      setTransaction({ kind: "error", message: friendlyError(error) });
    }
  }

  const action = burnerPaused ? (
    <button className="primary" disabled>Redemptions paused</button>
  ) : cost > MAX_HARDWARE_SUPPLY ? (
    <button className="primary" disabled>Hardware sold out</button>
  ) : !account ? (
    <button className="primary" onClick={connect}>Connect wallet</button>
  ) : !approved ? (
    <button className="primary" onClick={approve}>Approve 1988</button>
  ) : (
    <button className="primary" onClick={redeem}>Burn {cost.toString()} and redeem</button>
  );

  return (
    <main className="claim-layout">
      <section className="control-panel">
        <div className="panel-inner">
          <img className="mark" src="/mark.png" alt="Jack Butcher" />

          <div className="introduction rule">
            <p>Burn 1988 to receive a new Hardware token on Ethereum.</p>
            <p>For each token minted, the cost increases by one.</p>
          </div>

          <div className="mint-panel rule">
            <button className="wallet" onClick={connect}>{shortAccount}</button>

            <div className="stat-row">
              <label htmlFor="maximum">Max burn amount:</label>
              <input
                id="maximum"
                inputMode="numeric"
                min={cost.toString()}
                value={maximum}
                onChange={(event) => setMaximum(event.target.value.replace(/\D/g, ""))}
              />
              <span className="owned">You own: <strong>{balance.toString()}</strong></span>
            </div>

            <div className="cost-row">
              <span>Current cost:</span>
              <strong>{cost.toString()}</strong>
            </div>

            {action}
            {pendingRedemption && (
              <a className="secondary button-link" href={`/claim?tx=${pendingRedemption.transactionHash}`}>View claim status</a>
            )}
            {!isConfigured && <p className="notice">Recovery build — contracts awaiting deployment.</p>}
            {transaction.kind !== "idle" && (
              <div className={`transaction ${transaction.kind}`}>
                {transaction.message}
                {"hash" in transaction && transaction.hash && (
                  <a
                    href={`${transaction.chain === "ethereum" ? "https://etherscan.io" : "https://basescan.org"}/tx/${transaction.hash}`}
                    target="_blank"
                    rel="noreferrer"
                  >View transaction ↗</a>
                )}
              </div>
            )}
          </div>

          <div className="explanation">
            <p>Choose the maximum amount you’re willing to burn.</p>
            <p>If the current price moves above your maximum before confirmation, the transaction reverts.</p>
            <p>You’ll only burn the current amount required when your transaction executes.</p>
          </div>

          <nav>
            <a href="/claim">Claim a previous burn ↗</a>
            <a href="https://opensea.io/item/base/0x82e30a63bccde3724877878a30c4977b52348198/1" target="_blank" rel="noreferrer">Purchase 1988 tokens ↗</a>
            <a href="https://etherscan.io/address/0x0c3dd3c403b6b5c0cce2a97f15820a4eae347fe7" target="_blank" rel="noreferrer">View Hardware contract ↗</a>
            <a href="https://arweave.net/VVC1zQDZ_n8FiJAB4ldx3TQAyUdZ9_nBrFZJz-UFhDY" target="_blank" rel="noreferrer">View permanent artwork ↗</a>
            <a href="/deploy">Deploy contracts ↗</a>
            <a href="/admin">Owner console ↗</a>
          </nav>
        </div>
      </section>

      <section className="gallery" aria-label="Hardware gallery">
        <CollectionLedger />
        {gallery.map((src) => <img key={src} src={src} alt="Hardware artwork" loading="lazy" />)}
      </section>
    </main>
  );
}

function CollectionLedger() {
  const [original1988Supply, setOriginal1988Supply] = useState<bigint>();
  const [available1988Supply, setAvailable1988Supply] = useState<bigint>();
  const [hardwareSupply, setHardwareSupply] = useState<bigint>();

  useEffect(() => {
    Promise.all([
      baseClient.readContract({ address: BURN_TOKEN, abi: erc1155Abi, functionName: "getTokenInfo", args: [1n] }),
      baseClient.readContract({ address: LEGACY_BURNER, abi: legacyBurnerAbi, functionName: "numCrosschainRedeemedTokens", args: [LEGACY_VECTOR] }),
      ethereumClient.readContract({ address: HARDWARE_COLLECTION, abi: hardwareCollectionAbi, functionName: "totalSupply" }),
      discoverDeploymentConfig({ requireActivation: true }),
    ]).then(async ([tokenInfo, legacyRedemptions, currentHardwareSupply, active]) => {
      const redemptionCount = active.burnerAddress
        ? await baseClient.readContract({ address: active.burnerAddress, abi: burnerAbi, functionName: "redemptionCount" })
        : legacyRedemptions;
      const burned1988 = (redemptionCount * (redemptionCount + 1n)) / 2n;
      setOriginal1988Supply(tokenInfo.totalMinted);
      setAvailable1988Supply(tokenInfo.totalMinted - burned1988);
      setHardwareSupply(currentHardwareSupply);
    }).catch(() => {
      // The gallery remains usable if a public RPC is temporarily unavailable.
    });
  }, []);

  return (
    <div className="collection-ledger" aria-label="Collection supply">
      <a className="collection-ledger-item" href="https://basescan.org/token/0x82e30a63bccde3724877878a30c4977b52348198?a=1" target="_blank" rel="noreferrer">
        <div className="collection-ledger-top"><span>Base / ERC-1155</span><span>2024</span></div>
        <div className="collection-ledger-main"><h2>1988</h2><span className="collection-year">2024</span></div>
        <div className="collection-supply"><span>Available supply</span><strong>{formatSupply(available1988Supply)} / {formatSupply(original1988Supply)}</strong></div>
      </a>
      <a className="collection-ledger-item" href="https://etherscan.io/token/0x0c3dd3c403b6b5c0cce2a97f15820a4eae347fe7" target="_blank" rel="noreferrer">
        <div className="collection-ledger-top"><span>Ethereum / ERC-721</span><span>2025</span></div>
        <div className="collection-ledger-main"><h2>Hardware</h2><span className="collection-year">2025</span></div>
        <div className="collection-supply"><span>Current supply</span><strong>{formatSupply(hardwareSupply)} / 544</strong></div>
      </a>
    </div>
  );
}

function formatSupply(value?: bigint) {
  return value === undefined ? "—" : new Intl.NumberFormat("en-US").format(value);
}

async function switchToBase() {
  if (!window.ethereum) throw new Error("No wallet detected");
  try {
    await window.ethereum.request({ method: "wallet_switchEthereumChain", params: [{ chainId: "0x2105" }] });
  } catch (error) {
    const code = (error as { code?: number }).code;
    if (code !== 4902) throw error;
    await window.ethereum.request({
      method: "wallet_addEthereumChain",
      params: [{
        chainId: "0x2105",
        chainName: "Base",
        nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
        rpcUrls: ["https://mainnet.base.org"],
        blockExplorerUrls: ["https://basescan.org"],
      }],
    });
  }
}

function friendlyError(error: unknown) {
  if (error instanceof Error) {
    if (error.message.includes("User rejected") || error.message.includes("rejected the request")) return "Transaction cancelled.";
    if (error.message.includes("SlippageExceeded")) return "The burn price moved above your maximum. Refresh and try again.";
    if (error.message.includes("SoldOut")) return "Hardware is sold out. No tokens were burned.";
    if (error.message.includes("EnforcedPause")) return "Hardware redemptions are paused. No tokens were burned.";
    if (error.message.includes("insufficient funds")) return "This wallet does not have enough ETH for gas.";
    return error.message.split("\n")[0];
  }
  return "Something went wrong. Please try again.";
}
